/** Section 21 - movement on the hex battlefield.
 *
 * Four movement classes, one reachability pass:
 *   walking   - a Dijkstra over free hexes, blocked by anything solid. It was a
 *               breadth-first search until mud arrived: a hex that costs two
 *               makes "first to reach it" and "cheapest way to it" different
 *               answers, and only the second is a movement rule;
 *   flying    - straight-line hex distance, obstacles ignored, landing hex free;
 *   teleport  - identical reach to flying but no path is drawn (no interception,
 *               no moat, and the renderer must not animate a walk);
 *   two-hex   - every candidate position needs BOTH its hexes free, including
 *               the intermediate ones, so the check lives inside the search.
 *
 * A moat hex is enterable but terminal: entering ends the move (and costs HP),
 * which is why the search never expands out of one.
 */

import {
  BF_HEXES, BF_WIDTH, F_FLYING, F_TELEPORTS, F_TWO_HEX,
  type BattleState, type Obstacle, type StackStore,
  hexCol, hexDistance, hexNeighbours, isAlive,
} from "./state.js";
import { effectiveSpeed } from "./order.js";
import { F2_BOUND } from "./flags.js";

const NB: number[] = new Array(6).fill(0);

export const UNREACHABLE = -1;

/**
 * Obstacle kinds that a unit cannot enter at all.
 *
 * `flies` is the one thing that varies: a Ward Wall is a wall of air that
 * stops feet and not wings, which is the whole difference between it and a
 * rock. Everything else answers the same for both.
 */
export function obstacleBlocks(o: Obstacle, flies = false): boolean {
  switch (o.kind) {
    case "rock": case "tree": case "tower": return true;
    case "wall": case "gate": return o.integrity > 0;
    case "forcefield": return !flies && o.integrity > 0;
    case "moat": case "sinking_sand": case "landmine": case "firewall": return false;
    // Ground, not cover. Both are crossable and both charge for it: see
    // `stepCost` for the mud and `applyHazardOnEnter` for the embers.
    case "mud": case "embers": return false;
  }
}

/**
 * What entering `hex` costs a walker, in movement points.
 *
 * One everywhere except mud, which is two - H5's swamp patch, and the reason
 * the walking pass below is a Dijkstra rather than the breadth-first search it
 * used to be. Flying and teleporting stacks never pay it: they are over the
 * ground rather than in it, which is the same exemption they already have from
 * every other obstacle.
 *
 * A cost of two rather than "you stop here" on purpose. Sinking Sand already means
 * "that is as far as you go", and a second obstacle with the same rule would be
 * a reskin; a slow lane is a different decision - worth crossing with a fast
 * stack, worth going round with a slow one, and worth standing behind either
 * way. It is also symmetric by construction: `field.ts` lays one board and both
 * armies fight over it.
 */
export const MUD_STEP_COST = 2;

export function hexStepCost(s: BattleState, hex: number): number {
  for (const o of s.obstacles) {
    if (o.hex === hex && o.kind === "mud") return MUD_STEP_COST;
  }
  return 1;
}

/** Ground that bites whoever stands in it, and the bite. Zero for everything else. */
export function hazardBite(s: BattleState, hex: number): number {
  let dmg = 0;
  for (const o of s.obstacles) {
    if (o.hex !== hex) continue;
    if (o.kind === "embers") dmg += o.power ?? EMBER_DAMAGE;
  }
  return dmg;
}

/** What a patch of embers costs a stack that stops in it, per creature-less blow. */
export const EMBER_DAMAGE = 25;

/**
 * Hard cover: `slot` is standing with a blocking prop between it and the open
 * field, so an arrow is that much less likely to find it.
 *
 * Any rock, tree or standing masonry touching one of the stack's own hexes
 * counts. It is deliberately not a line-of-sight test against the shooter: H5
 * gives cover for being *in* cover rather than for the geometry of one shot,
 * the board is fifteen columns wide, and a per-shooter trace would make the
 * same stack's defence depend on which archer was asking - which is a rule
 * nobody could read off the screen.
 */
export function inHardCover(s: BattleState, slot: number): boolean {
  const own: number[] = [0, 0];
  const k = stackHexes(s.stacks, slot, own);
  const nb: number[] = [0, 0, 0, 0, 0, 0];
  for (let m = 0; m < k; m++) {
    const kn = hexNeighbours(own[m]!, nb);
    for (let i = 0; i < kn; i++) {
      const o = obstacleAt(s, nb[i]!);
      if (o !== null && obstacleBlocks(o)) return true;
    }
  }
  return false;
}

/**
 * Obstacles that end a move rather than refuse it. A moat and a patch of
 * sinking_sand are both "you may go in, and that is as far as you go"; the moat
 * additionally costs blood, which `execute.ts` applies on entry.
 */
export function endsMove(s: BattleState, hex: number): boolean {
  for (const o of s.obstacles) {
    if (o.hex !== hex) continue;
    if (o.kind === "moat") return true;
    if (o.kind === "sinking_sand" && o.integrity > 0) return true;
  }
  return false;
}

export function obstacleAt(s: BattleState, hex: number): Obstacle | null {
  for (const o of s.obstacles) if (o.hex === hex) return o;
  return null;
}

export function isMoat(s: BattleState, hex: number): boolean {
  for (const o of s.obstacles) if (o.hex === hex && o.kind === "moat") return true;
  return false;
}

/** The hexes a stack physically stands on. Returns the count written to `out`. */
export function stackHexes(st: StackStore, i: number, out: number[]): number {
  const h = st.hex[i]!;
  out[0] = h;
  if ((st.flags[i]! & F_TWO_HEX) === 0) return 1;
  out[1] = h - st.facing[i]!;
  return 2;
}

/** The tail hex for a two-hex stack standing at `head`, or -1 if that is off the row. */
export function tailHex(head: number, facing: number): number {
  const c = hexCol(head) - facing;
  if (c < 0 || c >= BF_WIDTH) return -1;
  return head - facing;
}

/** hex -> occupying slot, -1 for empty. Obstacles are not in here. */
export function buildOccupancy(s: BattleState, out: Int32Array): void {
  out.fill(-1);
  const st = s.stacks;
  const scratch: number[] = [0, 0];
  for (let i = 0; i < st.n; i++) {
    if (!isAlive(st, i)) continue;
    const k = stackHexes(st, i, scratch);
    for (let m = 0; m < k; m++) {
      const h = scratch[m]!;
      if (h >= 0 && h < BF_HEXES) out[h] = i;
    }
  }
}

export function newOccupancy(s: BattleState): Int32Array {
  const occ = new Int32Array(BF_HEXES);
  buildOccupancy(s, occ);
  return occ;
}

function hexFree(s: BattleState, occ: Int32Array, hex: number, forSlot: number): boolean {
  if (hex < 0 || hex >= BF_HEXES) return false;
  const who = occ[hex]!;
  if (who >= 0 && who !== forSlot) return false;
  const o = obstacleAt(s, hex);
  if (o === null) return true;
  const flies = forSlot >= 0 && forSlot < s.stacks.n
    && (s.stacks.flags[forSlot]! & (F_FLYING | F_TELEPORTS)) !== 0;
  if (o.kind === "forcefield") return !obstacleBlocks(o, flies);
  // An open gate is a doorway, not masonry. `siege.gateOpen` is set when the
  // gate is battered down or shaken open, and is true from the first round of
  // an assault the town could not bar - without this read it meant nothing to
  // the movement rules and a standing gatehouse sealed the wall either way.
  if (o.kind === "gate" && s.siege !== null && s.siege.gateOpen) return true;
  return !obstacleBlocks(o);
}

/** Can `slot` stand with its head on `head`? Two-hex stacks need their tail too. */
export function canOccupy(s: BattleState, occ: Int32Array, slot: number, head: number): boolean {
  if (!hexFree(s, occ, head, slot)) return false;
  if ((s.stacks.flags[slot]! & F_TWO_HEX) === 0) return true;
  const t = tailHex(head, s.stacks.facing[slot]!);
  if (t < 0) return false;
  return hexFree(s, occ, t, slot);
}

export interface Reach {
  /** Steps to each hex, or UNREACHABLE. Indexed by head hex. */
  readonly dist: Int32Array;
  /** Predecessor head hex for path reconstruction, or -1. */
  readonly prev: Int32Array;
}

/**
 * Reachable head positions for `slot`. Flying and teleporting stacks measure in
 * straight-line hex distance; walkers breadth-first through free hexes.
 */
export function computeReach(s: BattleState, slot: number, occ: Int32Array): Reach {
  const dist = new Int32Array(BF_HEXES).fill(UNREACHABLE);
  const prev = new Int32Array(BF_HEXES).fill(-1);
  const st = s.stacks;
  const from = st.hex[slot]!;
  const speed = effectiveSpeed(s, slot);
  dist[from] = 0;

  /**
   * Held in the branches: the reach is the hex it is standing on and nothing
   * else (Binding). Before the flying test on purpose - a bound Dragon is bound
   * - and after `dist[from] = 0`, so the stack can still attack everything
   * already beside it, still retaliate, and still defend. It simply cannot go
   * anywhere, which is the whole of what Binding does.
   */
  if ((st.flags2[slot]! & F2_BOUND) !== 0) return { dist, prev };

  const flies = (st.flags[slot]! & (F_FLYING | F_TELEPORTS)) !== 0;
  if (flies) {
    for (let h = 0; h < BF_HEXES; h++) {
      if (h === from) continue;
      const d = hexDistance(from, h);
      if (d > speed) continue;
      if (!canOccupy(s, occ, slot, h)) continue;
      dist[h] = d;
      prev[h] = from;
    }
    return { dist, prev };
  }

  /**
   * Dijkstra over the free hexes, not a breadth-first search: a mud patch costs
   * two and everything else costs one, so a plain queue would settle a hex at
   * the first cost that reached it rather than at the cheapest, and a route that
   * skirted the mud would lose to one that waded through it.
   *
   * Costs are small integers on a 165-cell board, so the "priority queue" is a
   * linear scan for the cheapest unsettled hex. That is 165 scans of 165 in the
   * worst case and it is called once per legality enumeration; a heap here would
   * be a faster answer to a question nobody is asking.
   */
  const settled = new Uint8Array(BF_HEXES);
  for (;;) {
    let h = -1;
    let best = Number.MAX_SAFE_INTEGER;
    for (let i = 0; i < BF_HEXES; i++) {
      if (settled[i] === 1 || dist[i] === UNREACHABLE) continue;
      if (dist[i]! < best) { best = dist[i]!; h = i; }
    }
    if (h < 0) break;
    settled[h] = 1;
    const d = dist[h]!;
    if (d >= speed) continue;
    if (h !== from && endsMove(s, h)) continue; // a moat or sinking_sand ends the move
    const k = hexNeighbours(h, NB);
    for (let m = 0; m < k; m++) {
      const nb = NB[m]!;
      if (settled[nb] === 1) continue;
      if (!canOccupy(s, occ, slot, nb)) continue;
      const cost = d + hexStepCost(s, nb);
      if (cost > speed) continue;
      if (dist[nb] !== UNREACHABLE && dist[nb]! <= cost) continue;
      dist[nb] = cost;
      prev[nb] = h;
    }
  }
  return { dist, prev };
}

/** Head-hex path from the stack's position to `target`, inclusive of `target`. */
export function pathTo(s: BattleState, slot: number, target: number, reach: Reach): number[] | null {
  if (target < 0 || target >= BF_HEXES) return null;
  if (reach.dist[target] === UNREACHABLE) return null;
  const from = s.stacks.hex[slot]!;
  if (target === from) return [];
  if ((s.stacks.flags[slot]! & F_TELEPORTS) !== 0) return [target];
  const out: number[] = [];
  let cur = target;
  let guard = 0;
  while (cur !== from && guard++ <= BF_HEXES) {
    out.push(cur);
    const p = reach.prev[cur]!;
    if (p < 0) return null;
    cur = p;
  }
  out.reverse();
  return out;
}

export function findPath(s: BattleState, slot: number, target: number): number[] | null {
  return pathTo(s, slot, target, computeReach(s, slot, newOccupancy(s)));
}

// ------------------------------------------------------------------ adjacency

export function stacksAdjacent(st: StackStore, a: number, b: number): boolean {
  const ha: number[] = [0, 0], hb: number[] = [0, 0];
  const ka = stackHexes(st, a, ha);
  const kb = stackHexes(st, b, hb);
  for (let i = 0; i < ka; i++) {
    for (let m = 0; m < kb; m++) if (hexDistance(ha[i]!, hb[m]!) === 1) return true;
  }
  return false;
}

/** Would `slot` standing with its head on `head` be adjacent to `target`? */
export function wouldBeAdjacent(s: BattleState, slot: number, head: number, target: number): boolean {
  const st = s.stacks;
  const mine: number[] = [head, -1];
  let km = 1;
  if ((st.flags[slot]! & F_TWO_HEX) !== 0) {
    const t = tailHex(head, st.facing[slot]!);
    if (t >= 0) { mine[1] = t; km = 2; }
  }
  const hb: number[] = [0, 0];
  const kb = stackHexes(st, target, hb);
  for (let i = 0; i < km; i++) {
    for (let m = 0; m < kb; m++) if (hexDistance(mine[i]!, hb[m]!) === 1) return true;
  }
  return false;
}

/** True when any living enemy stands next to `slot`; blocks ordinary shooting. */
export function meleeBlocked(s: BattleState, slot: number): boolean {
  const st = s.stacks;
  for (let i = 0; i < st.n; i++) {
    if (i === slot || !isAlive(st, i)) continue;
    if (st.side[i] === st.side[slot]) continue;
    if (stacksAdjacent(st, i, slot)) return true;
  }
  return false;
}

/**
 * A standing wall or gate segment between shooter and target halves the damage.
 * Sampled along the straight line rather than traced exactly - the battlefield
 * is fifteen columns wide and walls are a vertical run, so a column test is both
 * correct here and immune to hex-rounding arguments.
 */
export function shotCrossesWall(s: BattleState, from: number, to: number): boolean {
  if (s.siege === null || !s.siege.active) return false;
  const lo = Math.min(hexCol(from), hexCol(to));
  const hi = Math.max(hexCol(from), hexCol(to));
  for (const o of s.obstacles) {
    if (o.kind !== "wall" && o.kind !== "gate") continue;
    if (o.integrity <= 0) continue;
    const c = hexCol(o.hex);
    if (c > lo && c < hi) return true;
  }
  return false;
}

/** Hexes from which `attacker` could strike `target`, given current occupancy. */
export function attackPositions(
  s: BattleState, attacker: number, target: number, reach: Reach, out: number[],
): number {
  let n = 0;
  const from = s.stacks.hex[attacker]!;
  if (wouldBeAdjacent(s, attacker, from, target)) out[n++] = from;
  for (let h = 0; h < BF_HEXES; h++) {
    if (h === from) continue;
    if (reach.dist[h] === UNREACHABLE) continue;
    if (!wouldBeAdjacent(s, attacker, h, target)) continue;
    out[n++] = h;
  }
  return n;
}
